import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  Settings, 
  Construction, 
  RefreshCw,
  AlertTriangle,
  CheckCircle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AppSettings({ user: initialUser }) {
  const navigate = useNavigate();
  const [user, setUser] = useState(initialUser);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Maintenance mode state
  const [maintenanceEnabled, setMaintenanceEnabled] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState(
    "The app is currently under maintenance. Please check back soon."
  );
  const [lastUpdated, setLastUpdated] = useState(null);

  // Use initial user from props, redirect if not teacher
  useEffect(() => {
    if (initialUser) {
      setUser(initialUser);
      if (initialUser.role !== 'teacher') {
        navigate('/');
      }
    }
  }, [initialUser, navigate]);

  useEffect(() => {
    if (user?.role === 'teacher') {
      fetchMaintenanceStatus();
    }
  }, [user]);

  const fetchMaintenanceStatus = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/maintenance-mode`, { withCredentials: true });
      setMaintenanceEnabled(response.data.enabled);
      setMaintenanceMessage(response.data.message || "The app is currently under maintenance. Please check back soon.");
      setLastUpdated(response.data.updated_at);
    } catch (error) {
      console.error('Error fetching maintenance status:', error);
      toast.error('Failed to load maintenance status');
    } finally {
      setLoading(false);
    }
  };

  const saveMaintenanceSettings = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/maintenance-mode`, {
        enabled: maintenanceEnabled,
        message: maintenanceMessage
      }, { withCredentials: true });
      
      toast.success(maintenanceEnabled ? 'Maintenance mode enabled' : 'Maintenance mode disabled');
      fetchMaintenanceStatus();
    } catch (error) {
      console.error('Error saving maintenance settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleString();
  };

  if (!user || user.role !== 'teacher') {
    return null;
  }

  return (
    <Layout user={user} setUser={setUser}>
      <div className="space-y-6" data-testid="app-settings-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">App Settings</h1>
            <p className="text-slate-500">Manage application-wide settings</p>
          </div>
          <Button onClick={fetchMaintenanceStatus} variant="outline" disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Maintenance Mode Card */}
        <Card className="border-2 border-amber-200">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <Construction className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <CardTitle>Maintenance Mode</CardTitle>
                  <CardDescription>
                    When enabled, users will see a maintenance page instead of the app
                  </CardDescription>
                </div>
              </div>
              {maintenanceEnabled ? (
                <Badge className="bg-amber-100 text-amber-800 border-amber-200">
                  <AlertTriangle className="w-3 h-3 mr-1" />
                  Active
                </Badge>
              ) : (
                <Badge variant="outline" className="text-green-600 border-green-200">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Inactive
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Toggle Switch */}
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div className="space-y-0.5">
                <Label htmlFor="maintenance-toggle" className="text-base font-medium">
                  Enable Maintenance Mode
                </Label>
                <p className="text-sm text-slate-500">
                  Teachers can still access the app when maintenance is enabled
                </p>
              </div>
              <Switch
                id="maintenance-toggle"
                checked={maintenanceEnabled}
                onCheckedChange={setMaintenanceEnabled}
                data-testid="maintenance-toggle"
              />
            </div>

            {/* Warning when enabled */}
            {maintenanceEnabled && (
              <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-800">Warning</p>
                  <p className="text-sm text-amber-700">
                    When you save with maintenance mode enabled, students will not be able to access the app.
                    Only teachers/admins can bypass this.
                  </p>
                </div>
              </div>
            )}

            {/* Custom Message */}
            <div className="space-y-2">
              <Label htmlFor="maintenance-message">Maintenance Message</Label>
              <Textarea
                id="maintenance-message"
                value={maintenanceMessage}
                onChange={(e) => setMaintenanceMessage(e.target.value)}
                placeholder="Enter the message users will see..."
                rows={3}
                className="resize-none"
                data-testid="maintenance-message-input"
              />
              <p className="text-xs text-slate-500">
                This message will be displayed to users when they try to access the app during maintenance.
              </p>
            </div>

            {/* Last Updated Info */}
            {lastUpdated && (
              <p className="text-sm text-slate-500">
                Last updated: {formatDate(lastUpdated)}
              </p>
            )}

            {/* Save Button */}
            <div className="flex justify-end pt-4 border-t">
              <Button 
                onClick={saveMaintenanceSettings} 
                disabled={saving}
                className={maintenanceEnabled ? 'bg-amber-600 hover:bg-amber-700' : ''}
                data-testid="save-maintenance-btn"
              >
                {saving ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Settings className="w-4 h-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
